// Which of a set of languages a person should be reading, before they have said.
//
// Shared by the public site, which moves an undecided visitor off its English
// home page (apps/landing/src/language.ts), and by the panels, which start in
// this language until an account says otherwise (catalogs.ts, bootLocale). One
// rule, so a visitor the site answered in Ukrainian is not met by an English
// sign-in screen one click later.
//
// It holds the RULE and nothing else - no DOM, no storage, no navigator - so it
// can be exercised without a page (scripts/place.test.mjs).

// Where each language is READ, as the time zones a browser reports from there.
// The stronger of the two signals, and the reason it is: somebody in Kyiv or in
// Warsaw is often running an English browser, and the clock still says where
// they are. A device sets its clock from where it is, so the signal also
// survives a VPN that puts the network address in another country. A language
// absent from this table is not ignored - it is matched by the browser's own
// language list, which is the weaker signal but the one the diaspora sets
// deliberately.
export const HOME_ZONES = {
  uk: /^Europe\/(Kyiv|Kiev|Uzhgorod|Uzhhorod|Zaporozhye|Zaporizhzhia|Simferopol)$/,
  pl: /^Europe\/Warsaw$/,
};

/**
 * The language to open for an undecided person: by where they are, then by what
 * they read. Returns nothing when neither signal names a language on offer.
 */
export function preferredLanguage(offered, zone, languages) {
  const here = offered.find((link) => HOME_ZONES[link.lang]?.test(zone));
  if (here) return here;
  // The browser's list is in the person's own order of preference, so it is
  // walked in that order - a browser that asks for English first is asking for
  // English, even when it names another language after it.
  for (const wanted of languages) {
    const base = wanted.split('-')[0].toLowerCase();
    const hit =
      offered.find((link) => link.lang.toLowerCase() === wanted.toLowerCase()) ??
      offered.find((link) => link.lang.toLowerCase() === base);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * The language a panel starts in, before any account is known.
 *
 * A pinned language (`?lng=`) wins: the public site's sign-in links carry the
 * language of the page they sit on, and a screenshot run pins one the same way.
 * Otherwise the clock and the browser's list decide, by the rule the site sends
 * its visitors by. `available` is what this build can render, so nothing it has
 * no words for is named; undefined leaves the caller to the browser's own tag.
 */
export function startLanguage(pinned, available, zone, languages) {
  if (pinned) return pinned;
  return preferredLanguage(
    available.map((lang) => ({ lang })),
    zone,
    languages,
  )?.lang;
}
